import { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext, GroupContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Shield, ShieldOff, Edit, Trash2, UserPlus, Mail, Clock, Send, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HLRA Group ID for special "Committee Member" label
const HLRA_GROUP_ID = '074d7a6c-df5e-4b48-8123-17aa2a2ae4f8';

export default function Users() {
  const { user: currentUser } = useContext(AuthContext);
  const { activeGroup } = useContext(GroupContext);
  const [users, setUsers] = useState([]);
  const [allUsersWithGroups, setAllUsersWithGroups] = useState([]); // For group membership info
  const [pendingUsers, setPendingUsers] = useState([]); // created + invited users
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false); // Track if current user is admin of active group
  const [groupMemberIds, setGroupMemberIds] = useState([]); // Track member IDs for the active group
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'committee',
    organization: ''
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    username: '',
    phone: '',
    role: 'committee',
    organization: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchUsersWithGroups();
  }, []);

  // Check if current user is a Group Admin for the active group
  // Also fetch group members for filtering the user list
  useEffect(() => {
    const checkGroupAdminAndMembers = async () => {
      if (!currentUser || !activeGroup) {
        setIsGroupAdmin(false);
        setGroupMemberIds([]);
        return;
      }
      
      // Super admins always have admin rights and see all users
      if (currentUser.role === 'super_admin') {
        setIsGroupAdmin(true);
        setGroupMemberIds([]); // Empty means show all users
        return;
      }
      
      try {
        // Fetch the group members to check current user's role and get member list
        const response = await axios.get(`${API}/groups/${activeGroup.id}/members`);
        const members = response.data;
        const currentMember = members.find(m => m.user_id === currentUser.id);
        setIsGroupAdmin(currentMember?.role === 'admin');
        
        // Store member IDs for filtering the user list
        setGroupMemberIds(members.map(m => m.user_id));
      } catch (error) {
        console.error('Failed to check group admin status:', error);
        setIsGroupAdmin(false);
        setGroupMemberIds([]);
      }
    };
    
    checkGroupAdminAndMembers();
  }, [currentUser, activeGroup]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const allUsers = response.data;
      setUsers(allUsers.filter(u => u.status === 'active'));
      setPendingUsers(allUsers.filter(u => u.status === 'created' || u.status === 'invited'));
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersWithGroups = async () => {
    try {
      const response = await axios.get(`${API}/users/with-groups`);
      setAllUsersWithGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch users with groups:', error);
    }
  };

  // Filter users based on current user's role
  // Super Admins see all users, Group Admins only see users in their group
  const filteredUsers = currentUser?.role === 'super_admin' || groupMemberIds.length === 0
    ? users
    : users.filter(u => groupMemberIds.includes(u.id));
  
  const filteredPendingUsers = currentUser?.role === 'super_admin' || groupMemberIds.length === 0
    ? pendingUsers
    : pendingUsers.filter(u => groupMemberIds.includes(u.id));

  // Helper to check if user's primary group is HLRA
  const isUserInHLRA = (userId) => {
    const userWithGroups = allUsersWithGroups.find(u => u.id === userId);
    if (!userWithGroups) return false;
    
    // Check if user's default_group_id is HLRA or if they have HLRA in their memberships
    if (userWithGroups.default_group_id === HLRA_GROUP_ID) return true;
    
    const memberships = userWithGroups.group_memberships || [];
    return memberships.some(m => m.group_id === HLRA_GROUP_ID);
  };

  const handleGrantAdmin = async (userId) => {
    if (!window.confirm('Grant admin rights to this user?')) return;
    
    try {
      await axios.post(`${API}/users/${userId}/grant-admin`);
      toast.success('Admin rights granted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant admin rights');
    }
  };

  const handleRevokeAdmin = async (userId) => {
    if (!window.confirm('Revoke admin rights from this user?')) return;
    
    try {
      await axios.post(`${API}/users/${userId}/revoke-admin`);
      toast.success('Admin rights revoked');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke admin rights');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      organization: user.organization || ''
    });
    setShowEditDialog(true);
    setResetPassword('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/users/${editingUser.id}`, editForm);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword.trim()) {
      toast.error('Please enter a temporary password');
      return;
    }
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      const res = await axios.post(`${API}/admin/users/${editingUser.id}/reset-password`, { password: resetPassword });
      toast.success(res.data.message || 'Password reset successfully');
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      // If user is a Group Admin (not super_admin), use the group-specific endpoint
      if (currentUser?.role !== 'super_admin' && isGroupAdmin && activeGroup) {
        const response = await axios.post(`${API}/groups/${activeGroup.id}/members/create`, {
          name: createForm.name,
          email: createForm.email,
          username: createForm.username || createForm.email.split('@')[0],
          phone: createForm.phone,
          role: createForm.role === 'external_org' ? 'external_org' : 'committee',
          organization: createForm.organization,
          group_role: 'member', // Default role in the group
          send_invitation: true // Send invitation immediately for group members
        });
        toast.success(`User created and added to ${activeGroup.name}! Invitation email sent.`);
      } else {
        // Super admin uses the standard endpoint
        const response = await axios.post(`${API}/users/create`, {
          ...createForm,
          send_invitation: false  // Don't send invitation on create
        });
        toast.success('User created successfully! You can now assign them to actions.');
      }
      
      setShowCreateDialog(false);
      setCreateForm({
        name: '',
        email: '',
        username: '',
        phone: '',
        role: 'committee',
        organization: ''
      });
      fetchUsers();
      fetchUsersWithGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleSendInvitation = async (userId) => {
    setSendingInvite(userId);
    try {
      await axios.post(`${API}/users/${userId}/resend-invite`);
      toast.success('Invitation sent successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role, organization, userId) => {
    // For committee role, show "Committee Member" only if user is in HLRA group
    // Otherwise show "Group Member"
    const committeeLabel = isUserInHLRA(userId) ? 'Committee Member' : 'Group Member';
    
    const roleConfig = {
      super_admin: { label: 'Super Admin', color: 'bg-purple-100 text-purple-700' },
      admin: { label: 'Admin', color: 'bg-blue-100 text-blue-700' },
      committee: { label: committeeLabel, color: 'bg-green-100 text-green-700' },
      external_org: { label: organization || 'External Partner', color: 'bg-orange-100 text-orange-700' }
    };
    
    const config = roleConfig[role] || roleConfig.committee;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      created: { label: 'Created', color: 'bg-gray-100 text-gray-700', icon: User },
      invited: { label: 'Invited', color: 'bg-yellow-100 text-yellow-700', icon: Mail },
      active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle }
    };
    
    const config = statusConfig[status] || statusConfig.created;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getInviteStatus = (user) => {
    if (user.status === 'created') {
      return <span className="text-xs text-gray-500">No login access yet</span>;
    }
    
    if (!user.invite_expires) return null;
    
    const expires = new Date(user.invite_expires);
    const now = new Date();
    const isExpired = now > expires;
    
    if (isExpired) {
      return <Badge className="bg-red-100 text-red-700">Invitation Expired</Badge>;
    }
    
    const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return <span className="text-xs text-yellow-600">Expires in {daysLeft} day(s)</span>;
  };

  return (
    <div className="space-y-6 fade-in" data-testid="users-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Users</h1>
          <p className="text-gray-600">Manage users and control their access</p>
          {isGroupAdmin && currentUser?.role !== 'super_admin' && activeGroup && (
            <p className="text-sm text-blue-600 mt-1">
              You are an admin of <strong>{activeGroup.name}</strong> group
            </p>
          )}
        </div>
        {(currentUser?.role === 'super_admin' || isGroupAdmin) && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-blue-500 to-green-500"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {currentUser?.role === 'super_admin' ? 'Create User' : `Add ${activeGroup?.name || 'Group'} Member`}
          </Button>
        )}
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active">
            {currentUser?.role === 'super_admin' ? 'Active Users' : `${activeGroup?.name || 'Group'} Members`} ({filteredUsers.length})
          </TabsTrigger>
          {(currentUser?.role === 'super_admin' || isGroupAdmin) && (
            <TabsTrigger value="pending">
              Pending Access ({filteredPendingUsers.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>
                {currentUser?.role === 'super_admin' ? 'Active Users' : `${activeGroup?.name || 'Group'} Members`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No members found in this group</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 border rounded-lg card-hover"
                      data-testid={`user-${user.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              {user.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            {user.username && (
                              <p className="text-xs text-gray-500 font-mono">@{user.username}</p>
                            )}
                            {user.phone && (
                              <p className="text-xs text-gray-500">{user.phone}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {getRoleBadge(user.role, user.organization, user.id)}
                        
                        <div className="flex flex-wrap gap-2 mt-3">
                          {/* Edit button for Super Admins, system Admins, and Group Admins */}
                          {(currentUser?.role === 'super_admin' || 
                            currentUser?.role === 'admin' ||
                            isGroupAdmin) && user.role !== 'super_admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          )}
                          
                          {/* Admin rights management - Super Admin only */}
                          {currentUser?.role === 'super_admin' && user.role !== 'super_admin' && (
                            <>
                              {user.role === 'admin' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRevokeAdmin(user.id)}
                                  className="text-red-600"
                                  data-testid={`revoke-admin-${user.id}`}
                                >
                                  <ShieldOff className="w-3 h-3 mr-1" />
                                  Revoke Admin
                                </Button>
                              ) : user.role === 'committee' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleGrantAdmin(user.id)}
                                  data-testid={`grant-admin-${user.id}`}
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  Grant Admin
                                </Button>
                              )}
                              
                              {/* Delete button - Super Admin only */}
                              {user.id !== currentUser.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600"
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Users Pending Access
              </CardTitle>
              <p className="text-sm text-gray-500">
                These users can be assigned to actions but don't have login access yet
              </p>
            </CardHeader>
            <CardContent>
              {filteredPendingUsers.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No users pending access</p>
              ) : (
                <div className="space-y-4">
                  {filteredPendingUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`p-4 border rounded-lg flex items-center justify-between ${
                        user.status === 'created' ? 'bg-gray-50' : 'bg-yellow-50'
                      }`}
                      data-testid={`pending-user-${user.id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <Avatar>
                          <AvatarFallback className={`${
                            user.status === 'created' ? 'bg-gray-200 text-gray-700' : 'bg-yellow-200 text-yellow-700'
                          }`}>
                            {user.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-medium text-gray-900">{user.name}</h3>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <p className="text-xs text-gray-500 font-mono">@{user.username}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getRoleBadge(user.role, user.organization, user.id)}
                            {getStatusBadge(user.status)}
                          </div>
                          <div className="mt-1">
                            {getInviteStatus(user)}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleSendInvitation(user.id)}
                          disabled={sendingInvite === user.id}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`send-invite-${user.id}`}
                        >
                          {sendingInvite === user.id ? (
                            <>Sending...</>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              {user.status === 'created' ? 'Send Invitation' : 'Resend Invitation'}
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                          data-testid={`edit-pending-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-pending-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                required
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                required
                data-testid="edit-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone (Optional)</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                data-testid="edit-phone-input"
              />
            </div>
            {currentUser?.role === 'super_admin' && editingUser?.id !== currentUser?.id && (
              <>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                    <SelectTrigger data-testid="edit-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="committee">Group Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="external_org">External Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.role === 'external_org' && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-org">Organization Name</Label>
                    <Input
                      id="edit-org"
                      value={editForm.organization}
                      onChange={(e) => setEditForm({...editForm, organization: e.target.value})}
                      placeholder="e.g., Berkeley Homes, RMG"
                      data-testid="edit-org-input"
                    />
                  </div>
                )}
              </>
            )}
            {currentUser?.role === 'super_admin' && editingUser?.id !== currentUser?.id && (
              <div className="border-t pt-4 mt-2">
                <Label className="text-sm font-medium text-gray-700">Reset Password</Label>
                <p className="text-xs text-gray-500 mb-2">Set a temporary password for this user. They should change it after logging in.</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter temporary password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="flex-1"
                    data-testid="reset-password-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetPassword}
                    disabled={resettingPassword || !resetPassword.trim()}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50 whitespace-nowrap"
                    data-testid="reset-password-button"
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset'}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="save-user-button">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              {currentUser?.role === 'super_admin' ? 'Create New User' : `Add Member to ${activeGroup?.name || 'Group'}`}
            </DialogTitle>
            <DialogDescription>
              {currentUser?.role === 'super_admin' 
                ? 'Create a user that can be assigned to actions. You can send them a login invitation later.'
                : `Create a new member for the ${activeGroup?.name || 'current'} group. They will receive an invitation email to set their password.`
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                placeholder="John Smith"
                required
                data-testid="create-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                placeholder="john@example.com"
                required
                data-testid="create-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-username">Username *</Label>
              <Input
                id="create-username"
                value={createForm.username}
                onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                placeholder="johnsmith"
                required
                data-testid="create-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-phone">Phone (Optional)</Label>
              <Input
                id="create-phone"
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                placeholder="+44 7xxx xxxxxx"
                data-testid="create-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({...createForm, role: v})}>
                <SelectTrigger data-testid="create-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="committee">Group Member</SelectItem>
                  {currentUser?.role === 'super_admin' && (
                    <SelectItem value="admin">Admin</SelectItem>
                  )}
                  <SelectItem value="external_org">External Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {createForm.role === 'external_org' && (
              <div className="space-y-2">
                <Label htmlFor="create-org">Organization Name *</Label>
                <Input
                  id="create-org"
                  value={createForm.organization}
                  onChange={(e) => setCreateForm({...createForm, organization: e.target.value})}
                  placeholder="e.g., Berkeley Homes, RMG, Southern Housing"
                  required
                  data-testid="create-org-input"
                />
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <p className="font-medium">What happens after creation?</p>
              <ul className="list-disc list-inside mt-1 text-blue-600">
                {currentUser?.role === 'super_admin' ? (
                  <>
                    <li>User can immediately be assigned to actions</li>
                    <li>User will receive email reminders for assigned actions</li>
                    <li>User will <strong>NOT</strong> have login access yet</li>
                    <li>When ready, click "Send Invitation" to grant login access</li>
                  </>
                ) : (
                  <>
                    <li>User will be added to the <strong>{activeGroup?.name}</strong> group</li>
                    <li>An invitation email will be sent automatically</li>
                    <li>User can set their password and login</li>
                    <li>User can then be assigned to actions in this group</li>
                  </>
                )}
              </ul>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="create-user-submit">
                <UserPlus className="w-4 h-4 mr-2" />
                {currentUser?.role === 'super_admin' ? 'Create User' : 'Add Member'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
